/**
 * Origin-scoped session teardown for the legacy PixelMe app.
 *
 * Ported from `frontend/src/utils/local-session.ts`. PixelMe serves two apps
 * from one origin - this one at `/` and the v2 app at `/v2/*` - so they share
 * `localStorage` and each can strand credentials the other wrote. Both apps
 * keep the same key inventory so a sign-out on either fully clears the origin.
 *
 * Since the Cognito migration this app no longer writes `access-token`: the
 * live token belongs to oidc-client-ts in sessionStorage. The key stays on the
 * list so that a browser carrying one from before the migration is cleaned up
 * on the next sign-out rather than keeping a stale Auth0 JWT indefinitely.
 */

/** Pre-Cognito credentials. No longer written; still cleared. */
const LEGACY_TOKEN_KEYS = ['access-token', 'old-access-token'];

/** Impersonation state, written by this app and read by both. */
const IMPERSONATION_KEYS = ['impersonate', 'impersonate_access_override', 'impersonating_email'];

/** Sticky per-tab flags derived from an authenticated session. */
const SESSION_FLAG_KEYS = [
  'pixelme:skip_permissions_flow',
  'pixelme:hub_redirect_committed',
  'pixelme:me_reauth_attempted',
  'pixelme:signup_app_context',
];

/** Signup/invitation hand-off state, only meaningful mid-flow. */
const SIGNUP_FLOW_KEYS = ['PIX_INVITATION_CODE', 'PIX_SIGNUP_USER_TYPE', 'signup-theme'];

/**
 * Account selection. Cleared here rather than by the `SIGN_OUT` redux action,
 * whose reducer also calls `window.location.reload()` as a side effect - see
 * `stores/store.js`. Reloading mid-sign-out races the redirect to hub's
 * platform logout, so `/sign-out` skips that dispatch and does the cleanup
 * directly.
 */
const ACCOUNT_SELECTION_KEYS = ['selected-account'];

function removeAll(store, keys) {
  keys.forEach((key) => {
    try {
      store.removeItem(key);
    } catch {
      // A single unavailable key must not abort the rest of the teardown.
    }
  });
}

/**
 * Remove stored credentials, leaving per-tab flags intact.
 *
 * This is the variant to use on a 401, where the token is known dead but the
 * surrounding flow is still running and its loop-protection flags must survive.
 *
 * Deliberately excludes the oidc-client-ts store: inside the running app that
 * is owned by the UserManager and cleared via `removeUser()`, which also
 * notifies subscribers. Deleting its key behind its back would desynchronise
 * in-memory state. `/sign-out` deletes those keys itself, before any
 * UserManager exists (`auth/signOut.js`).
 */
export function clearLocalCredentials() {
  if (typeof window === 'undefined') {
    return;
  }

  removeAll(window.localStorage, LEGACY_TOKEN_KEYS);
  removeAll(window.localStorage, IMPERSONATION_KEYS);
}

/**
 * Remove every credential and auth-derived flag this origin can hold.
 *
 * This is the sign-out variant: the flow is ending, so the sticky flags go too.
 * Safe to call more than once and safe to call when already signed out.
 */
export function clearLocalSessionState() {
  if (typeof window === 'undefined') {
    return;
  }

  clearLocalCredentials();
  removeAll(window.sessionStorage, SESSION_FLAG_KEYS);
  removeAll(window.localStorage, SIGNUP_FLOW_KEYS);
  removeAll(window.localStorage, ACCOUNT_SELECTION_KEYS);
}

export const localSessionKeys = {
  legacyTokens: LEGACY_TOKEN_KEYS,
  impersonation: IMPERSONATION_KEYS,
  sessionFlags: SESSION_FLAG_KEYS,
  signupFlow: SIGNUP_FLOW_KEYS,
  accountSelection: ACCOUNT_SELECTION_KEYS,
};
